/**
 * The two side panes can be dragged wider or narrower, within bounds, and the
 * choice is kept in the browser: it is about this screen, not the agents, so
 * it does not belong in host settings the way the roster's arrangement does.
 */
export interface WidthBounds {
  readonly min: number;
  readonly max: number;
  readonly initial: number;
}

export const SIDEBAR_WIDTH: WidthBounds = { min: 240, max: 480, initial: 320 };
export const DETAILS_WIDTH: WidthBounds = { min: 300, max: 640, initial: 380 };

/** A width inside the bounds; anything that is not a number is the initial one. */
export function clampWidth(bounds: WidthBounds, value: number): number {
  if (!Number.isFinite(value)) return bounds.initial;
  return Math.round(Math.min(bounds.max, Math.max(bounds.min, value)));
}

/** What was stored last time, if it still makes sense; else the initial width. */
export function loadWidth(bounds: WidthBounds, stored: string | null): number {
  if (stored == null || stored.trim().length === 0) return bounds.initial;
  return clampWidth(bounds, Number(stored));
}

/** One step of the keyboard: a resize handle moves this far per arrow press. */
export const KEY_STEP = 16;

/**
 * How the page is laid out at a width (client-app §6). A phone shows the
 * roster or the chat, one at a time; a narrow screen, an unfolded foldable
 * or a small window, keeps the roster beside the chat but lets the side pane
 * lie over the chat; a wide one has room for all three. The phone's line is
 * Android's own: a screen under 600dp is a phone, a foldable's inner screen
 * and a tablet are not, and a foldable crosses it as it opens and closes.
 */
export type Layout = "phone" | "narrow" | "wide";
export const PHONE_MAX_WIDTH = 600;
export const OVERLAY_MAX_WIDTH = 900;
export const PHONE_QUERY = `(max-width: ${PHONE_MAX_WIDTH}px)`;
export const OVERLAY_QUERY = `(max-width: ${OVERLAY_MAX_WIDTH}px)`;

export function layoutAt(width: number): Layout {
  if (width <= PHONE_MAX_WIDTH) return "phone";
  if (width <= OVERLAY_MAX_WIDTH) return "narrow";
  return "wide";
}
